import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  signal,
  viewChildren,
} from '@angular/core';

import { SuggestionHistoryItem, TimestampedSentence } from '../../../models/session.types';

interface TimeMarker {
  time: number;
  label: string;
  top: number;
}

interface TimelineEventLayout {
  trackId: string;
  relativeTime: number;
  eventY: number;
  cardOffset: number;
  height: number;
  wrapperHeight: number;
  connectorHeight: number;
}

interface TimelinePin {
  id: string;
  top: number;
  type: 'agent' | 'customer' | 'suggestion';
}

type NormalizedTranscript = TimestampedSentence & {
  trackId: string;
  relativeTime: number;
};

type NormalizedSuggestion = SuggestionHistoryItem & {
  trackId: string;
  relativeTime: number;
};

@Component({
  selector: 'app-session-timeline',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './session-timeline.component.html',
  styleUrl: './session-timeline.component.scss',
})
export class SessionTimelineComponent {
  private readonly defaultPixelsPerSecond = 28;
  private readonly minPixelsPerSecond = 12;
  private readonly maxPixelsPerSecond = 72;
  private readonly laneTopPadding = 24;
  private readonly laneBottomPadding = 48;
  private readonly cardGap = 14;
  private readonly connectorInset = 20;
  private readonly minTranscriptHeight = 88;
  private readonly minSuggestionHeight = 104;
  private readonly niceMarkerIntervals = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800];

  readonly transcription = input<TimestampedSentence[]>([]);
  readonly suggestionHistory = input<SuggestionHistoryItem[]>([]);
  readonly sessionCreatedAt = input<string>('');
  readonly sessionDurationSeconds = input<number>(0);

  private readonly transcriptCards = viewChildren<ElementRef<HTMLElement>>('transcriptCard');
  private readonly suggestionCards = viewChildren<ElementRef<HTMLElement>>('suggestionCard');

  protected readonly pixelsPerSecond = signal(this.defaultPixelsPerSecond);
  protected readonly transcriptLayouts = signal<TimelineEventLayout[]>([]);
  protected readonly suggestionLayouts = signal<TimelineEventLayout[]>([]);

  protected readonly sessionStartMs = computed(() => {
    const createdAt = this.sessionCreatedAt();
    if (!createdAt) return 0;
    return new Date(createdAt).getTime();
  });

  protected readonly normalizedTranscriptions = computed<NormalizedTranscript[]>(() => {
    const items = this.transcription() || [];
    const sessionStart = this.sessionStartMs();

    return items
      .map((item, index) => ({
        ...item,
        trackId: item.id || `transcript-${index}`,
        relativeTime: this.toRelativeSeconds(item.timestamp, sessionStart),
      }))
      .sort((a, b) => a.relativeTime - b.relativeTime);
  });

  protected readonly normalizedSuggestions = computed<NormalizedSuggestion[]>(() => {
    const items = this.suggestionHistory() || [];
    const sessionStart = this.sessionStartMs();

    return items
      .map((item, index) => ({
        ...item,
        trackId: `${item.timestamp}-${index}`,
        relativeTime: this.parseAndNormalizeTimestamp(item.timestamp, sessionStart),
      }))
      .sort((a, b) => a.relativeTime - b.relativeTime);
  });

  protected readonly hasData = computed(() => {
    return this.normalizedTranscriptions().length > 0 || this.normalizedSuggestions().length > 0;
  });

  protected readonly transcriptCount = computed(() => this.normalizedTranscriptions().length);
  protected readonly suggestionCount = computed(() => this.normalizedSuggestions().length);

  protected readonly totalDurationSeconds = computed(() => {
    const explicitDuration = this.sessionDurationSeconds() || 0;
    const transcriptMax = Math.max(
      0,
      ...this.normalizedTranscriptions().map((item) => item.relativeTime),
    );
    const suggestionMax = Math.max(
      0,
      ...this.normalizedSuggestions().map((item) => item.relativeTime),
    );

    return Math.max(explicitDuration, transcriptMax, suggestionMax);
  });

  protected readonly formattedDuration = computed(() => {
    return this.formatTimeFromSeconds(this.totalDurationSeconds());
  });

  protected readonly timeMarkers = computed<TimeMarker[]>(() => {
    const durationSeconds = this.totalDurationSeconds();
    if (durationSeconds <= 0) return [];

    const targetSpacingPx = 112;
    const secondsPerMarker = targetSpacingPx / this.pixelsPerSecond();
    const intervalSeconds =
      this.niceMarkerIntervals.find((interval) => interval >= secondsPerMarker) ??
      this.niceMarkerIntervals[this.niceMarkerIntervals.length - 1];

    const markers: TimeMarker[] = [];
    for (let time = 0; time <= durationSeconds; time += intervalSeconds) {
      markers.push({
        time,
        label: this.formatTimeFromSeconds(time),
        top: this.timeToY(time),
      });
    }

    if (markers.length === 0 || markers[markers.length - 1]?.time !== durationSeconds) {
      markers.push({
        time: durationSeconds,
        label: this.formatTimeFromSeconds(durationSeconds),
        top: this.timeToY(durationSeconds),
      });
    }

    return markers;
  });

  protected readonly eventPins = computed<TimelinePin[]>(() => {
    const transcriptPins = this.transcriptLayouts().map((item) => {
      const transcript = this.normalizedTranscriptions().find(
        (transcription) => transcription.trackId === item.trackId,
      );

      return {
        id: item.trackId,
        top: item.eventY,
        type: transcript?.speaker === 'Agent' ? ('agent' as const) : ('customer' as const),
      };
    });

    const suggestionPins = this.suggestionLayouts().map((item) => ({
      id: item.trackId,
      top: item.eventY,
      type: 'suggestion' as const,
    }));

    return [...transcriptPins, ...suggestionPins].sort((a, b) => a.top - b.top);
  });

  protected readonly timelineHeight = computed(() => {
    const durationBottom = this.timeToY(this.totalDurationSeconds()) + this.laneBottomPadding;
    const transcriptBottom = this.transcriptLayouts().reduce(
      (max, item) => Math.max(max, item.eventY + item.wrapperHeight),
      0,
    );
    const suggestionBottom = this.suggestionLayouts().reduce(
      (max, item) => Math.max(max, item.eventY + item.wrapperHeight),
      0,
    );

    return Math.max(durationBottom, transcriptBottom, suggestionBottom, 240);
  });

  protected readonly zoomPercent = computed(() => {
    const range = this.maxPixelsPerSecond - this.minPixelsPerSecond;
    const current = this.pixelsPerSecond() - this.minPixelsPerSecond;
    return Math.round((current / range) * 100);
  });

  constructor() {
    effect((onCleanup) => {
      this.normalizedTranscriptions();
      this.normalizedSuggestions();
      this.pixelsPerSecond();
      this.transcriptCards();
      this.suggestionCards();

      const frame = requestAnimationFrame(() => this.recomputeLayouts());
      onCleanup(() => cancelAnimationFrame(frame));
    });
  }

  protected formatTimeFromSeconds(seconds: number): string {
    const roundedSeconds = Math.max(0, Math.round(seconds));
    const mins = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  protected setZoom(rawValue: number): void {
    const nextValue = Number.isFinite(rawValue) ? rawValue : this.defaultPixelsPerSecond;
    this.pixelsPerSecond.set(
      Math.max(this.minPixelsPerSecond, Math.min(this.maxPixelsPerSecond, nextValue)),
    );
  }

  protected zoomIn(): void {
    this.setZoom(this.pixelsPerSecond() + 8);
  }

  protected zoomOut(): void {
    this.setZoom(this.pixelsPerSecond() - 8);
  }

  protected resetZoom(): void {
    this.pixelsPerSecond.set(this.defaultPixelsPerSecond);
  }

  private recomputeLayouts(): void {
    const transcripts = this.normalizedTranscriptions();
    const suggestions = this.normalizedSuggestions();

    const transcriptHeights = this.measureHeights(
      transcripts.map((item) => item.trackId),
      this.transcriptCards(),
      (index) => this.estimateTranscriptHeight(transcripts[index]),
    );
    const suggestionHeights = this.measureHeights(
      suggestions.map((item) => item.trackId),
      this.suggestionCards(),
      (index) => this.estimateSuggestionHeight(suggestions[index]),
    );

    this.transcriptLayouts.set(
      this.buildLayouts(transcripts, transcriptHeights, this.minTranscriptHeight),
    );
    this.suggestionLayouts.set(
      this.buildLayouts(suggestions, suggestionHeights, this.minSuggestionHeight),
    );
  }

  private buildLayouts<T extends { trackId: string; relativeTime: number }>(
    items: T[],
    heights: Map<string, number>,
    minHeight: number,
  ): TimelineEventLayout[] {
    let nextCardTop = this.laneTopPadding;

    return items.map((item) => {
      const eventY = this.timeToY(item.relativeTime);
      const height = Math.max(minHeight, heights.get(item.trackId) ?? minHeight);
      const cardTop = Math.max(eventY, nextCardTop);
      const cardOffset = cardTop - eventY;
      const connectorHeight = cardOffset + this.connectorInset;
      const wrapperHeight = cardOffset + height;

      nextCardTop = cardTop + height + this.cardGap;

      return {
        trackId: item.trackId,
        relativeTime: item.relativeTime,
        eventY,
        cardOffset,
        height,
        wrapperHeight,
        connectorHeight,
      };
    });
  }

  private measureHeights(
    trackIds: string[],
    elements: readonly ElementRef<HTMLElement>[],
    fallback: (index: number) => number,
  ): Map<string, number> {
    return new Map(
      trackIds.map((trackId, index) => {
        const measuredHeight = elements[index]?.nativeElement.getBoundingClientRect().height;
        return [trackId, Math.ceil(measuredHeight || fallback(index))];
      }),
    );
  }

  private estimateTranscriptHeight(item: NormalizedTranscript | undefined): number {
    if (!item) return this.minTranscriptHeight;
    return Math.max(this.minTranscriptHeight, 72 + Math.ceil(item.text.length / 56) * 20);
  }

  private estimateSuggestionHeight(item: NormalizedSuggestion | undefined): number {
    if (!item) return this.minSuggestionHeight;
    const explanationLength = item.explanation?.length || 0;
    return Math.max(
      this.minSuggestionHeight,
      84 + Math.ceil((item.suggestion.length + explanationLength) / 64) * 18,
    );
  }

  private timeToY(seconds: number): number {
    return this.laneTopPadding + Math.max(0, seconds) * this.pixelsPerSecond();
  }

  private toRelativeSeconds(timestamp: number, sessionStartMs: number): number {
    if (timestamp > 946684800000) {
      return Math.max(0, (timestamp - sessionStartMs) / 1000);
    }
    if (timestamp > 946684800) {
      return Math.max(0, timestamp - sessionStartMs / 1000);
    }
    if (timestamp > 1000000) {
      return timestamp / 1000;
    }
    return timestamp;
  }

  private parseAndNormalizeTimestamp(timestamp: string | number, sessionStartMs: number): number {
    if (typeof timestamp === 'number') {
      return this.toRelativeSeconds(timestamp, sessionStartMs);
    }

    const parsed = Date.parse(timestamp);
    if (!Number.isNaN(parsed)) {
      return Math.max(0, (parsed - sessionStartMs) / 1000);
    }

    return 0;
  }
}
